import {
  makeStyles,
  RACCard,
  RACSearchDropdown,
  RACTextField,
  Typography,
} from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  INPUT_MAX_LENGTH,
  PoFormField,
  PoFormLabel,
  VALIDATIONS,
} from '../../constants/constants';
import { PurchaseOrderFormValues } from '../../types/types';
import { isNonEmptyValue } from '../../utils/utils';

export interface LocalSourceItemDetailsProps {
  purchaseOrder?: PurchaseOrderFormValues | null;
  isViewMode?: boolean;
  index?: number;
  showHeader?: boolean;
  canAddProduct?: boolean;
  onAddProduct?: () => void;
}
const useStyles = makeStyles((theme: any) => ({
  root: {
    marginBottom: '1.5rem',
  },
  row: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    marginTop: theme.typography.pxToRem(8),
  },
  cardContentContainer: {
    padding: '1.25rem',
    display: 'flex',
    gap: theme.typography.pxToRem(16),
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  field: {
    width: theme.typography.pxToRem(200),
  },
  extraLargeField: {
    minWidth: theme.typography.pxToRem(600),
  },
  addProductLink: {
    color: '#2f80ed',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  disabledLink: {
    cursor: 'default',
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));

export const LocalSourceItemDetails = ({
  purchaseOrder,
  isViewMode,
  index = 0,
  showHeader = true,
  canAddProduct = true,
  onAddProduct,
}: LocalSourceItemDetailsProps) => {
  const classes = useStyles();
  const { setValue, errors } = useFormContext();
  const [condition, setCondition] = useState({
    value: '',
    label: 'Specify condition',
  });
  const getFieldName = useCallback(
    (fieldName: PoFormField) =>
      index === 0 ? fieldName : `products[${index}].${fieldName}`,
    [index]
  );

  useEffect(() => {
    if (purchaseOrder && purchaseOrder.itemCondition) {
      setCondition({
        value: purchaseOrder.itemCondition,
        label: purchaseOrder.itemCondition,
      });
      setValue(
        getFieldName(PoFormField.Condition),
        purchaseOrder.itemCondition,
        {
          shouldValidate: true,
        }
      );
    }
    if (purchaseOrder) {
      if (purchaseOrder.supplier) {
        setValue(getFieldName(PoFormField.Supplier), purchaseOrder.supplier);
      }
      if (purchaseOrder.brand) {
        setValue(getFieldName(PoFormField.Brand), purchaseOrder.brand);
      }
      if (purchaseOrder.model) {
        setValue(getFieldName(PoFormField.Model), purchaseOrder.model);
      }
      if (purchaseOrder.description) {
        setValue(
          getFieldName(PoFormField.Description),
          purchaseOrder.description
        );
      }
      if (purchaseOrder.supplierOrderNo) {
        setValue(
          getFieldName(PoFormField.SupplierOrderNo),
          purchaseOrder.supplierOrderNo
        );
      }
    }
  }, [getFieldName, index, purchaseOrder, setValue]);

  const onConditionChanged = ({
    value,
    label,
  }: {
    value: string;
    label: string;
  }) => {
    setCondition({ value, label });
    setValue(getFieldName(PoFormField.Condition), value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const addProduct = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (canAddProduct) {
      onAddProduct?.();
    }
  };

  return (
    <div className={classes.root}>
      {showHeader && (
        <div className={classes.row}>
          <Typography noWrap variant="h4">
            Item Details
          </Typography>
          {!isViewMode && (
            <Typography variant="h5">
              <a
                href="#"
                className={`${classes.addProductLink} ${
                  canAddProduct ? '' : classes.disabledLink
                }`}
                onClick={addProduct}
                aria-disabled={!canAddProduct}
              >
                +Add Product
              </a>
            </Typography>
          )}
        </div>
      )}
      <RACCard>
        <div className={classes.cardContentContainer}>
          <span className={classes.field}>
            <Controller
              name={getFieldName(PoFormField.Supplier)}
              rules={{
                required: true,
                maxLength: 240,
                validate: (value) => isNonEmptyValue(value),
              }}
              render={({ onChange, value, name }) => (
                <RACTextField
                  disabled={isViewMode}
                  required
                  type="text"
                  label={PoFormLabel.Supplier}
                  onChange={onChange}
                  value={value}
                  name={name}
                  errorMessage={
                    get(errors, getFieldName(PoFormField.Supplier))
                      ? VALIDATIONS[PoFormField.Supplier]
                      : ''
                  }
                />
              )}
            />
          </span>
          <span className={classes.field}>
            <Controller
              name={getFieldName(PoFormField.Brand)}
              rules={{
                required: true,
                maxLength: INPUT_MAX_LENGTH,
                validate: (value) => isNonEmptyValue(value),
              }}
              render={({ onChange, value, name }) => (
                <RACTextField
                  disabled={isViewMode}
                  required
                  type="text"
                  label={PoFormLabel.Brand}
                  onChange={onChange}
                  value={value}
                  name={name}
                  errorMessage={
                    get(errors, getFieldName(PoFormField.Brand))
                      ? VALIDATIONS[PoFormField.Brand]
                      : ''
                  }
                />
              )}
            />
          </span>
          <span className={classes.field}>
            <Controller
              name={getFieldName(PoFormField.Model)}
              rules={{
                required: true,
                maxLength: INPUT_MAX_LENGTH,
                validate: (value) => isNonEmptyValue(value),
              }}
              render={({ onChange, value, name }) => (
                <RACTextField
                  disabled={isViewMode}
                  required
                  type="text"
                  label={PoFormLabel.Model}
                  onChange={onChange}
                  value={value}
                  name={name}
                  errorMessage={
                    get(errors, getFieldName(PoFormField.Model))
                      ? VALIDATIONS[PoFormField.Model]
                      : ''
                  }
                />
              )}
            />
          </span>
          <span className={classes.field}>
            <Controller
              name={getFieldName(PoFormField.Condition)}
              rules={{
                required: true,
              }}
              render={({ name }) => {
                return (
                  <RACSearchDropdown
                    name={name}
                    inputLabel={PoFormLabel.Condition}
                    options={[
                      { value: 'New', label: 'New' },
                      { value: 'Refurbished', label: 'Refurbished' },
                    ]}
                    OnChange={onConditionChanged}
                    required
                    disabled={isViewMode}
                    value={condition}
                    errormessage={
                      get(errors, getFieldName(PoFormField.Condition))
                        ? `${PoFormLabel.Condition} is required`
                        : ''
                    }
                  />
                );
              }}
            />
          </span>
          <span className={classes.field}>
            <Controller
              name={getFieldName(PoFormField.SupplierOrderNo)}
              rules={{
                maxLength: 150,
                validate: (value) => !value || isNonEmptyValue(value),
              }}
              render={({ onChange, value, name }) => (
                <RACTextField
                  disabled={isViewMode}
                  type="text"
                  label={PoFormLabel.SupplierOrderNo}
                  onChange={onChange}
                  value={value}
                  name={name}
                  errorMessage={
                    get(errors, getFieldName(PoFormField.SupplierOrderNo))
                      ? VALIDATIONS[PoFormField.SupplierOrderNo]
                      : ''
                  }
                />
              )}
            />
          </span>
          <span className={classes.extraLargeField}>
            <Controller
              name={getFieldName(PoFormField.Description)}
              rules={{
                required: true,
                maxLength: 255,
                validate: (value) => isNonEmptyValue(value),
              }}
              render={({ onChange, value, name }) => (
                <RACTextField
                  disabled={isViewMode}
                  required
                  type="text"
                  label={PoFormLabel.Description}
                  onChange={onChange}
                  value={value}
                  name={name}
                  errorMessage={
                    get(errors, getFieldName(PoFormField.Description))
                      ? VALIDATIONS[PoFormField.Description]
                      : ''
                  }
                />
              )}
            />
          </span>
        </div>
      </RACCard>
    </div>
  );
};
